/**
 * Remediation chat params: entity_type, entity_id, activeThread, activeWorkspace.
 * Uses _chat API (threads and workspaces). For future use with agent HTTP/WebSocket.
 */

import { getAuthHeaders } from '../core';
import { config } from '../../config';

export const REMEDIATION_ENTITY_TYPE = 'org-remediation';

/**
 * Builds entity_id for remediation chat: org + '-' + remediation id.
 */
export function buildEntityId(org: string, remediation: string): string {
  return `${org}-${remediation}`;
}

export interface Workspace {
  author_id: string;
  time: number;
  is_active: boolean;
  context: Record<string, unknown>;
  type: string;
  config: Record<string, unknown>;
  data: unknown[];
  irn: string;
  _id?: string;
  [key: string]: unknown;
}

export interface RemediationChatParams {
  entity_type: string;
  entity_id: string;
  activeThread: string | null;
  activeWorkspace: Workspace | null;
}

function buildChatBaseUrl(portfolio: string, org: string, entityType: string, entityId: string): string {
  const apiBase = (config.apiBase || '').trim();
  if (!apiBase) throw new Error('Missing API config (apiBase). Set REACT_APP_API_BASE in .env.');
  return `${apiBase}/_chat/${portfolio}/${org}/${entityType}/${entityId}`;
}

/**
 * Gets the active thread id for the remediation chat entity.
 * GET threads, pick one with is_active; if none, POST to create a new thread.
 */
export async function getActiveThread(
  portfolio: string,
  org: string,
  remediation: string
): Promise<string | null> {
  const entityType = REMEDIATION_ENTITY_TYPE;
  const entityId = buildEntityId(org, remediation);
  const baseUrl = buildChatBaseUrl(portfolio, org, entityType, entityId);
  const headers = await getAuthHeaders();

  const listRes = await fetch(baseUrl, { method: 'GET', headers });
  const threadsList = (await listRes.json().catch(() => ({}))) as { items?: { _id: string; is_active?: boolean }[] };
  const items = Array.isArray(threadsList?.items) ? threadsList.items : [];

  const activeT = items.find((t) => t.is_active);
  if (activeT) return activeT._id;

  const createRes = await fetch(baseUrl, { method: 'POST', headers });
  const { success, document } = (await createRes.json().catch(() => ({}))) as {
    success?: boolean;
    document?: { _id: string };
  };
  if (success && document?._id) return document._id;
  return null;
}

/**
 * Gets the active workspace for the given thread.
 * GET workspaces for the thread, then pick the one with is_active or the last one.
 */
export async function getActiveWorkspace(
  portfolio: string,
  org: string,
  remediation: string,
  activeThreadId: string
): Promise<Workspace | null> {
  const entityType = REMEDIATION_ENTITY_TYPE;
  const entityId = buildEntityId(org, remediation);
  const apiBase = (config.apiBase || '').trim();
  if (!apiBase) throw new Error('Missing API config (apiBase). Set REACT_APP_API_BASE in .env.');
  const url = `${apiBase}/_chat/${portfolio}/${org}/${entityType}/${entityId}/${activeThreadId}/workspaces`;
  const headers = await getAuthHeaders();

  const res = await fetch(url, { method: 'GET', headers });
  const data = (await res.json().catch(() => ({}))) as { items?: Workspace[] };
  const workspaces = Array.isArray(data?.items) ? data.items : [];
  if (workspaces.length === 0) return null;

  const active = workspaces.find((w) => w.is_active);
  return active ?? workspaces[workspaces.length - 1] ?? null;
}

/**
 * Fetches all params needed for the remediation chat loop: entity_type, entity_id, activeThread, activeWorkspace.
 */
export async function getRemediationChatParams(
  portfolio: string,
  org: string,
  remediation: string
): Promise<RemediationChatParams> {
  const entity_type = REMEDIATION_ENTITY_TYPE;
  const entity_id = buildEntityId(org, remediation);
  const activeThread = await getActiveThread(portfolio, org, remediation);
  const activeWorkspace = activeThread
    ? await getActiveWorkspace(portfolio, org, remediation, activeThread)
    : null;

  return {
    entity_type,
    entity_id,
    activeThread,
    activeWorkspace,
  };
}
